import React, { useEffect, useState, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import API from './utils/api';
import * as Sentry from '@sentry/react';
import { trackPageView } from './utils/analytics';
import { initTWADetection } from './utils/twa';
initTWADetection(); // persist TWA flag before React Router changes document.referrer
import { AuthProvider, useAuth } from './context/AuthContext';
import CookieBanner from './components/CookieBanner';
import DreamyAmbience from './components/DreamyAmbience';
import LandingPage from './pages/LandingPage';
import AdLandingPage from './pages/AdLandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import GenerateStoryPage from './pages/GenerateStoryPage';
import StoryLibraryPage from './pages/StoryLibraryPage';
import StoryReaderPage from './pages/StoryReaderPage';
import ChildrenPage from './pages/ChildrenPage';
import PricingPage from './pages/PricingPage';
import SettingsPage from './pages/SettingsPage';
import SharedStoryPage from './pages/SharedStoryPage';
import GiftStoryPage from './pages/GiftStoryPage';
import GiftSuccessPage from './pages/GiftSuccessPage';
import GiftRedeemPage from './pages/GiftRedeemPage';
import ApprovalQueuePage from './pages/ApprovalQueuePage';
import ContactPage from './pages/ContactPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import FAQPage from './pages/FAQPage';
import AboutPage from './pages/AboutPage';
import AppDownloadPage from './pages/AppDownloadPage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import AffiliateTermsPage from './pages/AffiliateTermsPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminSupport from './pages/admin/AdminSupport';
import AdminGiftOrders from './pages/admin/AdminGiftOrders';
import AdminStories from './pages/admin/AdminStories';
import AdminRevenue from './pages/admin/AdminRevenue';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import AdminVoiceProfiles from './pages/admin/AdminVoiceProfiles';
import AdminSeries from './pages/admin/AdminSeries';
import SeriesPage from './pages/SeriesPage';
import SeriesDetailPage from './pages/SeriesDetailPage';
import CharactersPage from './pages/CharactersPage';
import ReadingReportPage from './pages/ReadingReportPage';
import ReportsIndexPage from './pages/ReportsIndexPage';
import YearInStoriesPage from './pages/YearInStoriesPage';
import FamilyAcceptPage from './pages/FamilyAcceptPage';
import CommunityPage from './pages/CommunityPage';
import CommunityStoryPage from './pages/CommunityStoryPage';
import AdminCommunity from './pages/admin/AdminCommunity';
import AdminReports from './pages/admin/AdminReports';
import AdminBroadcast from './pages/admin/AdminBroadcast';
import AdminBlog from './pages/admin/AdminBlog';
import AdminBlogEditor from './pages/admin/AdminBlogEditor';
import AdminLogs from './pages/admin/AdminLogs';
import AdminSettings from './pages/admin/AdminSettings';
import AdminAffiliates from './pages/admin/AdminAffiliates';
import AdminAffiliateDetail from './pages/admin/AdminAffiliateDetail';
import AffiliateApplyPage from './pages/AffiliateApplyPage';
import AffiliateDashboardPage from './pages/AffiliateDashboardPage';
import AffiliateLandingPage from './pages/AffiliateLandingPage';
import BlogPage from './pages/BlogPage';
import GuidePage from './pages/GuidePage';
import ReferralPage from './pages/ReferralPage';
import BlogPostPage from './pages/BlogPostPage';
import OnboardingPage from './pages/OnboardingPage';
import MaintenancePage from './pages/MaintenancePage';
import {
  ThemePageRouter, LanguagePageRouter, AgeGroupPageRouter,
  OccasionPageRouter, NamePageRouter,
  LessonPageRouter, StoryLengthPageRouter, GenderPageRouter,
  RelationshipPageRouter, CountryPageRouter, HolidayPageRouter,
  SiblingsPageRouter, GiftPageRouter, ComparisonPageRouter,
  BilingualPageRouter, AnimalPageRouter, SettingPageRouter, MoodPageRouter,
  NameThemePageRouter, NameAgePageRouter, ThemeLanguagePageRouter,
  ThemesIndexPage, LanguagesIndexPage, NamesIndexPage, FreePage,
  AnimalsIndexPage, SettingsIndexPage, MoodsIndexPage,
} from './pages/SEOLandingPage';
import SuspendedPage from './pages/SuspendedPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import GrandparentViewPage from './pages/GrandparentViewPage';
import useSettings from './utils/useSettings';
import { isNative } from './utils/capacitor';
import NativeEntry from './components/native/NativeEntry';
import NativeBridge from './components/native/NativeBridge';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="spinner-border text-purple" role="status" style={{color:'#6f42c1'}}>
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );
  return user ? children : <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return children;
  // Honour ?redirect=/?next= so purchase intent (e.g. checkout from
  // /pricing) survives the login/register bounce for already-authed users too.
  const params = new URLSearchParams(location.search);
  const next = params.get('redirect') || params.get('next');
  if (next && next.startsWith('/') && !next.startsWith('//')) {
    return <Navigate to={next} />;
  }
  // New users (hasCompletedOnboarding === false) go to onboarding.
  // Returning users go to dashboard.
  return <Navigate to={user.hasCompletedOnboarding ? '/dashboard' : '/onboarding'} />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <div className="spinner-border" style={{ color: '#6f42c1' }}></div>
    </div>
  );
  // Preserve the deep link like PrivateRoute does, so an admin opening a
  // bookmarked /admin/* URL while logged out lands back on it after signing in.
  if (!user) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} />;
  if (!user.isAdmin) return <Navigate to="/dashboard" />;
  return children;
};

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// ── Shared banner renderer ─────────────────────────────────────────────────
function GlobalBanner({ msg, bg, icon, onDismiss }) {
  if (!msg) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 99999,
      background: bg, color: 'white',
      padding: '12px 20px', textAlign: 'center',
      fontSize: '14px', fontWeight: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    }}>
      <i className={`bi ${icon}`}></i>
      {msg}
      <button onClick={onDismiss} style={{
        background: 'none', border: 'none', color: 'white',
        cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px',
      }}>×</button>
    </div>
  );
}

// ── Global rate-limit banner (429) ─────────────────────────────────────────
// Fires on api:ratelimit events from the axios interceptor.
function RateLimitBanner() {
  const [msg, setMsg] = useState(null);
  // This used to `return () => clearTimeout(t)` from the event listener. A
  // listener's return value is discarded by the browser — it is not a useEffect
  // cleanup — so that timer was never cleared. Each rate-limit event stacked
  // another timeout, and an earlier one would then dismiss a LATER message
  // before the user had read it (two events 4s apart left the second visible
  // for 1s). Dismissing by hand left a stray timer too. ApiErrorBanner below
  // already does this properly with a ref; this now matches it.
  const timerRef = useRef(null);
  const dismiss = useCallback(() => {
    setMsg(null);
    clearTimeout(timerRef.current);
  }, []);
  useEffect(() => {
    const handler = (e) => {
      setMsg(e.detail?.message || 'Too many requests. Please wait a moment and try again.');
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setMsg(null), 5000);
    };
    window.addEventListener('api:ratelimit', handler);
    return () => {
      window.removeEventListener('api:ratelimit', handler);
      clearTimeout(timerRef.current);
    };
  }, []);
  return <GlobalBanner msg={msg} bg="#7c3aed" icon="bi-clock" onDismiss={dismiss} />;
}

// ── Global server/network error banner ────────────────────────────────────
// Fires on api:error events (network failures, 5xx server errors).
// Components don't need to handle these — the banner is the fallback UI.
function ApiErrorBanner() {
  const [msg, setMsg] = useState(null);
  const timerRef = useRef(null);
  const dismiss = useCallback(() => {
    setMsg(null);
    clearTimeout(timerRef.current);
  }, []);
  useEffect(() => {
    const handler = (e) => {
      setMsg(e.detail?.message || 'Something went wrong. Please try again.');
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setMsg(null), 6000);
    };
    window.addEventListener('api:error', handler);
    return () => {
      window.removeEventListener('api:error', handler);
      clearTimeout(timerRef.current);
    };
  }, []);
  return <GlobalBanner msg={msg} bg="#dc2626" icon="bi-exclamation-circle" onDismiss={dismiss} />;
}

// ── Affiliate cookie tracker ───────────────────────────────────────────────
// Reads ?aff=CODE from the URL, saves it as a 30-day cookie, and notifies
// the backend to count the click. Runs once on first page load.
function AffiliateTracker() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const aff = params.get('aff');
    if (!aff) return;
    // Save cookie for 30 days
    const secure = window.location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `dba=${encodeURIComponent(aff.toUpperCase())};max-age=${30 * 24 * 60 * 60};path=/;SameSite=Lax${secure}`;
    // Track in GA4
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'affiliate_link_clicked', { affiliate_code: aff.toUpperCase() });
    }
    // Log click server-side (fire-and-forget)
    API.post('/affiliate/click', { code: aff, landingPage: window.location.pathname }).catch(() => {});
  }, []);
  return null;
}

function AppRoutes() {
  const { settings } = useSettings();
  const maintenance = settings.maintenanceMode;
  const { user } = useAuth();
  const location = useLocation();

  // Always allow /login so an admin can authenticate during maintenance.
  // Once they log in their isAdmin flag bypasses the check below.
  const isLoginRoute = location.pathname === '/login';

  if (maintenance?.enabled && !user?.isAdmin && !isLoginRoute) {
    return <MaintenancePage message={maintenance?.message} />;
  }

  return (
    <>
      <DreamyAmbience />
      <NativeBridge />
      <AffiliateTracker />
      <RateLimitBanner />
      <ApiErrorBanner />
      <RouteTracker />
      <ScrollToTop />
      {/* Cookie consent is web-only — the native app has no browser cookies / GA. */}
      {!isNative() && <CookieBanner />}
      <div className="dream-page" key={location.pathname}>
      {/* Scoped inside the router so a page crash takes down that page rather
          than the whole app, and clears itself on navigation. */}
      <RouteErrorBoundary routeKey={location.pathname}>
      <Routes>
        {/* Native builds skip the marketing website and open into the app shell. */}
        <Route path="/" element={isNative() ? <NativeEntry /> : <LandingPage />} />
        <Route path="/welcome" element={<AdLandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/onboarding" element={<PrivateRoute><OnboardingPage /></PrivateRoute>} />
        <Route path="/checkout/success" element={<PrivateRoute><CheckoutSuccessPage /></PrivateRoute>} />
        <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
        <Route path="/dashboard/generate" element={<PrivateRoute><GenerateStoryPage /></PrivateRoute>} />
        <Route path="/dashboard/library" element={<PrivateRoute><StoryLibraryPage /></PrivateRoute>} />
        <Route path="/dashboard/story/:id" element={<PrivateRoute><StoryReaderPage /></PrivateRoute>} />
        <Route path="/dashboard/children" element={<PrivateRoute><ChildrenPage /></PrivateRoute>} />
        <Route path="/dashboard/approval-queue" element={<PrivateRoute><ApprovalQueuePage /></PrivateRoute>} />
        <Route path="/dashboard/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
        <Route path="/shared/:token" element={<SharedStoryPage />} />
        <Route path="/referral/:token" element={<ReferralPage />} />
        <Route path="/gift" element={settings.featureFlags?.giftStory ? <GiftStoryPage /> : <Navigate to="/" />} />
        <Route path="/gift/success" element={settings.featureFlags?.giftStory ? <GiftSuccessPage /> : <Navigate to="/" />} />
        <Route path="/gift/redeem/:token" element={<GiftRedeemPage />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/app" element={<AppDownloadPage />} />
        <Route path="/download" element={<Navigate to="/app" replace />} />
        <Route path="/cookies" element={<CookiePolicyPage />} />
        <Route path="/affiliate/terms" element={<AffiliateTermsPage />} />
        {/* Admin routes */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/admin/users/:id" element={<AdminRoute><AdminUserDetail /></AdminRoute>} />
        <Route path="/admin/support" element={<AdminRoute><AdminSupport /></AdminRoute>} />
        <Route path="/admin/gifts" element={<AdminRoute><AdminGiftOrders /></AdminRoute>} />
        <Route path="/admin/stories" element={<AdminRoute><AdminStories /></AdminRoute>} />
        <Route path="/admin/revenue" element={<AdminRoute><AdminRevenue /></AdminRoute>} />
        <Route path="/admin/voices" element={<AdminRoute><AdminVoiceProfiles /></AdminRoute>} />
        <Route path="/admin/series" element={<AdminRoute><AdminSeries /></AdminRoute>} />
        <Route path="/dashboard/series" element={settings.featureFlags?.storySeries ? <PrivateRoute><SeriesPage /></PrivateRoute> : <Navigate to="/dashboard" />} />
        <Route path="/dashboard/series/:id" element={settings.featureFlags?.storySeries ? <PrivateRoute><SeriesDetailPage /></PrivateRoute> : <Navigate to="/dashboard" />} />
        <Route path="/dashboard/characters" element={<PrivateRoute><CharactersPage /></PrivateRoute>} />
        <Route path="/dashboard/children/:id/report" element={<PrivateRoute><ReadingReportPage /></PrivateRoute>} />
        <Route path="/dashboard/children/:id/wrapped" element={<PrivateRoute><YearInStoriesPage /></PrivateRoute>} />
        <Route path="/family/accept/:token" element={<FamilyAcceptPage />} />
        <Route path="/grandparent/:token" element={<GrandparentViewPage />} />
        <Route path="/dashboard/reports" element={<PrivateRoute><ReportsIndexPage /></PrivateRoute>} />
        <Route path="/community" element={settings.featureFlags?.community ? <CommunityPage /> : <Navigate to="/" />} />
        <Route path="/community/:slug" element={settings.featureFlags?.community ? <CommunityStoryPage /> : <Navigate to="/" />} />
        <Route path="/admin/community"  element={<AdminRoute><AdminCommunity /></AdminRoute>} />
        <Route path="/admin/reports"    element={<AdminRoute><AdminReports /></AdminRoute>} />
        <Route path="/admin/broadcast" element={<AdminRoute><AdminBroadcast /></AdminRoute>} />
        <Route path="/admin/blog" element={<AdminRoute><AdminBlog /></AdminRoute>} />
        <Route path="/admin/blog/new" element={<AdminRoute><AdminBlogEditor /></AdminRoute>} />
        <Route path="/admin/blog/:id/edit" element={<AdminRoute><AdminBlogEditor /></AdminRoute>} />
        <Route path="/admin/logs" element={<AdminRoute><AdminLogs /></AdminRoute>} />
        <Route path="/admin/settings" element={<AdminRoute><AdminSettings /></AdminRoute>} />
        <Route path="/admin/affiliates" element={<AdminRoute><AdminAffiliates /></AdminRoute>} />
        <Route path="/admin/affiliates/:id" element={<AdminRoute><AdminAffiliateDetail /></AdminRoute>} />
        <Route path="/affiliate" element={<AffiliateLandingPage />} />
        <Route path="/affiliate/apply" element={<PrivateRoute><AffiliateApplyPage /></PrivateRoute>} />
        <Route path="/affiliate/dashboard" element={<PrivateRoute><AffiliateDashboardPage /></PrivateRoute>} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        {/* ── Programmatic SEO pages ── */}
        <Route path="/bedtime-stories/themes"              element={<ThemesIndexPage />} />
        <Route path="/bedtime-stories/themes/:theme"       element={<ThemePageRouter />} />
        <Route path="/bedtime-stories/languages"           element={<LanguagesIndexPage />} />
        <Route path="/bedtime-stories/languages/:language" element={<LanguagePageRouter />} />
        <Route path="/bedtime-stories/age/:age"            element={<AgeGroupPageRouter />} />
        <Route path="/bedtime-stories/occasions/:occasion" element={<OccasionPageRouter />} />
        <Route path="/bedtime-stories/for"                 element={<NamesIndexPage />} />
        <Route path="/bedtime-stories/for/:name"                element={<NamePageRouter />} />
        <Route path="/bedtime-stories/lessons/:lesson"          element={<LessonPageRouter />} />
        <Route path="/bedtime-stories/length/:length"           element={<StoryLengthPageRouter />} />
        <Route path="/bedtime-stories/gender/:gender"           element={<GenderPageRouter />} />
        <Route path="/bedtime-stories/from/:rel"                element={<RelationshipPageRouter />} />
        <Route path="/bedtime-stories/country/:country"         element={<CountryPageRouter />} />
        <Route path="/bedtime-stories/holidays/:holiday"        element={<HolidayPageRouter />} />
        <Route path="/bedtime-stories/siblings/:sibling"        element={<SiblingsPageRouter />} />
        <Route path="/bedtime-stories/gifts/:gift"              element={<GiftPageRouter />} />
        <Route path="/bedtime-stories/type/:comp"               element={<ComparisonPageRouter />} />
        <Route path="/bedtime-stories/bilingual/:bili"          element={<BilingualPageRouter />} />
        <Route path="/bedtime-stories/free"                     element={<FreePage />} />
        <Route path="/bedtime-stories/animals"                  element={<AnimalsIndexPage />} />
        <Route path="/bedtime-stories/animals/:animal"          element={<AnimalPageRouter />} />
        <Route path="/bedtime-stories/settings"                 element={<SettingsIndexPage />} />
        <Route path="/bedtime-stories/settings/:setting"        element={<SettingPageRouter />} />
        <Route path="/bedtime-stories/mood"                     element={<MoodsIndexPage />} />
        <Route path="/bedtime-stories/mood/:mood"               element={<MoodPageRouter />} />
        <Route path="/bedtime-stories/for/:name/:theme"         element={<NameThemePageRouter />} />
        <Route path="/bedtime-stories/for/:name/age/:age"       element={<NameAgePageRouter />} />
        <Route path="/bedtime-stories/themes/:theme/in/:language" element={<ThemeLanguagePageRouter />} />
        <Route path="/maintenance" element={settings.maintenanceMode?.enabled ? <MaintenancePage /> : <Navigate to="/" />} />
        <Route path="/suspended" element={<SuspendedPage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </RouteErrorBoundary>
      </div>
    </>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={
      <div className="d-flex flex-column justify-content-center align-items-center vh-100 text-center p-4">
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌙</div>
        <h4 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>Something went wrong</h4>
        <p className="text-muted mb-4">We've been notified and are looking into it.</p>
        <button className="btn btn-dream rounded-pill px-4" onClick={() => window.location.href = '/'}>
          Go to Home
        </button>
      </div>
    }>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
